import { ENDPOINT_TOLERANCE } from './topology';
import { simulationTrackNodeId } from './simulationTrackNetworkView';
import type { TrackGraph } from './simulationGraph';
import type { TrackNodeAnnotation, TrackNodeLandmarkRole } from '../types';

const ROLE_NONE: TrackNodeLandmarkRole = 'none';

/** Find the annotation whose position matches a world point within tolerance. */
export function findTrackNodeAnnotation(
  annotations: TrackNodeAnnotation[],
  x: number,
  y: number,
  tolerance = ENDPOINT_TOLERANCE
): TrackNodeAnnotation | undefined {
  const tol2 = tolerance * tolerance;
  let best: TrackNodeAnnotation | undefined;
  let bestDist2 = Infinity;
  for (const annotation of annotations) {
    const dx = annotation.x - x;
    const dy = annotation.y - y;
    const dist2 = dx * dx + dy * dy;
    if (dist2 <= tol2 && dist2 < bestDist2) {
      best = annotation;
      bestDist2 = dist2;
    }
  }
  return best;
}

/** Annotation for a live track node, or defaults when none has been saved yet. */
export function annotationForTrackNode(
  annotations: TrackNodeAnnotation[],
  node: { x: number; y: number; nodeId: string }
): TrackNodeAnnotation {
  return (
    findTrackNodeAnnotation(annotations, node.x, node.y) ?? {
      x: node.x,
      y: node.y,
      name: node.nodeId,
      role: ROLE_NONE,
    }
  );
}

export type ResolvedTrackNodeLandmarks = {
  entryLandmarkId?: string;
  exitLandmarkId?: string;
  entryFoupCapacity?: number;
  exitFoupCapacity?: number;
};

/** Synthetic load-port ids emitted when Entry / Exit track nodes are assigned. */
export const SYNTHETIC_ENTRY_LOAD_PORT_ID = 'LP-ENTRY';
export const SYNTHETIC_EXIT_LOAD_PORT_ID = 'LP-EXIT';

/** Default FOUP hold capacity for Entry / Exit gate ports. */
export const DEFAULT_GATE_FOUP_CAPACITY = 10;

function readPositiveCapacity(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/** Map persisted entry/exit landmarks onto current graph node ids. */
export function resolveTrackNodeLandmarks(
  graph: TrackGraph,
  annotations: TrackNodeAnnotation[],
  tolerance = ENDPOINT_TOLERANCE
): ResolvedTrackNodeLandmarks {
  const tol2 = tolerance * tolerance;
  let entryLandmarkId: string | undefined;
  let exitLandmarkId: string | undefined;
  let entryFoupCapacity: number | undefined;
  let exitFoupCapacity: number | undefined;

  for (const annotation of annotations) {
    if (annotation.role !== 'entry' && annotation.role !== 'exit') continue;

    let bestIndex = -1;
    let bestDist2 = Infinity;
    graph.nodes.forEach((node, index) => {
      const dx = node.x - annotation.x;
      const dy = node.y - annotation.y;
      const dist2 = dx * dx + dy * dy;
      if (dist2 <= tol2 && dist2 < bestDist2) {
        bestIndex = index;
        bestDist2 = dist2;
      }
    });
    if (bestIndex < 0) continue;

    const nodeId = simulationTrackNodeId(bestIndex);
    const capacity = readPositiveCapacity(annotation.foupCapacity);
    if (annotation.role === 'entry') {
      entryLandmarkId = nodeId;
      entryFoupCapacity = capacity;
    }
    if (annotation.role === 'exit') {
      exitLandmarkId = nodeId;
      exitFoupCapacity = capacity;
    }
  }

  return { entryLandmarkId, exitLandmarkId, entryFoupCapacity, exitFoupCapacity };
}

const DEFAULT_NODE_ID_RE = /^(?:track-node-(\d+)|NODE-(\d+))$/i;

/** Whether an annotation has any user-authored data worth persisting. */
export function isMeaningfulTrackNodeAnnotation(annotation: TrackNodeAnnotation): boolean {
  if (
    annotation.role === 'entry' ||
    annotation.role === 'exit' ||
    annotation.role === 'junction' ||
    annotation.isJunction
  ) {
    return true;
  }
  const name = annotation.name.trim();
  return name.length > 0 && !DEFAULT_NODE_ID_RE.test(name);
}

/** User-facing label for a track node (custom name, or "Node N"). */
export function trackNodeDisplayName(
  annotation: Pick<TrackNodeAnnotation, 'name'>,
  nodeId: string
): string {
  const name = annotation.name.trim();
  if (name && !DEFAULT_NODE_ID_RE.test(name)) return name;
  const match = DEFAULT_NODE_ID_RE.exec(nodeId) ?? DEFAULT_NODE_ID_RE.exec(name);
  if (!match) return nodeId;
  // track-node-0 → Node 1; NODE-1 → Node 1
  const legacyIndex = match[1];
  const nodeIndex = match[2];
  if (legacyIndex != null) return `Node ${Number(legacyIndex) + 1}`;
  return `Node ${Number(nodeIndex)}`;
}
